import React, { Suspense, useEffect, useState } from "react";
import NavBar from "@/components/NavBar";
import BottomNav from "@/components/BottomNav";
import Footer from "@/components/Footer";
import LegalConsentModal from "@/components/LegalConsentModal";
import UrgentAlertModal from "@/components/UrgentAlertModal";
import RouteChatNotifier from "@/components/RouteChatNotifier";
import { useUser } from "@/context/UserContext";
import { submitConsents, getLegalTerms } from "@/lib/api";
import { toast } from "sonner";
import { Outlet, useLocation } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import useTenantTheme from "@/hooks/useTenantTheme";
import GlobalPinModal from "@/components/GlobalPinModal";
import UpdatePrompt from "@/components/UpdatePrompt";
import ManagingCompanyBanner from "@/components/ManagingCompanyBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import PageLoader from "@/components/PageLoader";

const Layout = () => {
  const { user, initialized, refresh } = useUser();
  const location = useLocation();
  const { t } = useI18n();
  useTenantTheme();
  const [currentVersion, setCurrentVersion] = useState(null);

  const isPublicRoute =
    location.pathname === "/login" ||
    location.pathname.startsWith("/auth") ||
    location.pathname.startsWith("/contratar") ||
    location.pathname.startsWith("/verify");

  // Fetch the current terms version once authenticated
  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      try {
        const terms = await getLegalTerms();
        if (active) setCurrentVersion(terms.version);
      } catch { /* gate stays closed until version is known */ }
    })();
    return () => { active = false; };
  }, [user]);

  const handleConsent = async (version, checks) => {
    try {
      await submitConsents(version, checks);
      await refresh();
      toast.success(t("layout.consents_registered"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("layout.consent_failed"));
    }
  };

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        {t("common.loading")}
      </div>
    );
  }

  // Block until consent matches the current version. A version bump (e.g. 1.0
  // → 1.1) re-shows the modal for every user.
  const userVersion = user?.consent_version;
  const needsConsent =
    !!user && !isPublicRoute && !!currentVersion && userVersion !== currentVersion;

  return (
    // Móvil: reserva el hueco de la barra de pestañas inferior flotante
    // (y de la zona segura del iPhone) para que no tape el contenido.
    <div className="min-h-screen bg-[#F7FAF8] flex flex-col pb-[calc(var(--bottom-nav-space)+0.5rem)] sm:pb-0">
      <GlobalPinModal />
      <UpdatePrompt />
      <NavBar />
      {!isPublicRoute && <ManagingCompanyBanner />}
      {!isPublicRoute && user && !needsConsent && <UrgentAlertModal enabled />}
      {!isPublicRoute && user && !needsConsent && user.role !== "pmst_guest" && <RouteChatNotifier />}
      {!isPublicRoute && (
        <LegalConsentModal open={needsConsent} onAccept={handleConsent} />
      )}
      <div className="flex-1">
        {/* When consent is pending, mask app content so users can't see data
            before accepting. The modal sits above this overlay. */}
        {needsConsent ? (
          <div data-testid="consent-blocker" className="max-w-md mx-auto px-4 py-12 text-center text-slate-500">
            {t("layout.awaiting_consents")}
          </div>
        ) : (
          // Si una pantalla falla o se está descargando, la barra de arriba
          // sigue ahí (antes la sustituía todo el spinner o el aviso de error).
          <ErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        )}
      </div>
      <Footer />
      {!isPublicRoute && !needsConsent && <BottomNav />}
    </div>
  );
};

export default Layout;
